import { isEmpty } from 'lodash';
import $http from '../../shared/http';

import randomColor from 'randomcolor';

const menSeriesColour = randomColor({ seed: '4dbd74' });
const womenSeriesColour = randomColor({ seed: '159aff' });
const totalSeriesColour = randomColor({ seed: 'fd7e14' });

const organisation_dashboard = {
  namespaced: true,
  state: () => {
    return {
      membershipRegistrationTrendsForDashboard: null,
      membershipByStatusesForDashboard: null,
      existingStatusNames: null,
      dashboardData: null,
      membershipByZonesForDashboard: null,
      membershipByMinistriesForDashboard: null,
      headCountsTrendForDashboard: null,
      givingsTrendForDashboard: null,
      serviceTypeTrendOption: null,
      visitorsRegistrationTrendForDashboard: null,
      serviceTypeOptionForGivingTrend: null,
      capturedAttendanceTrendForDashboard: null,
      serviceTypeOptionForCapturedAttendanceTrend: null,
    };
  },

  getters: {
    getDashboardData: (state) => state.dashboardData,

    seriesColours: () => ({ menSeriesColour, womenSeriesColour, totalSeriesColour }),

    getMembershipRegistrationTrendsForDashboard: (state) => {
      if (!state.membershipRegistrationTrendsForDashboard) return {};
      else {
        const datasets = [
          {
            label: 'Brothers',
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: menSeriesColour,
            data: state.membershipRegistrationTrendsForDashboard.map(
              (month) => month.data.brothers
            ),
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          },
          {
            label: 'Sisters',
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: womenSeriesColour,
            data: state.membershipRegistrationTrendsForDashboard.map((month) => month.data.sisters),
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          },
          {
            label: 'Total',
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: totalSeriesColour,
            data: state.membershipRegistrationTrendsForDashboard.map((month) => month.data.total),
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          },
        ];

        const labels = state.membershipRegistrationTrendsForDashboard.map(
          (month) => month.monthCustom
        );

        return { datasets, labels };
      }
    },

    getVisitorsRegistrationTrendForDashboard: (state) => {
      if (!state.visitorsRegistrationTrendForDashboard) return {};
      else {
        const datasets = [
          {
            label: 'Men',
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: menSeriesColour,
            data: state.visitorsRegistrationTrendForDashboard.map(
              (month) => month.data.maleVisitors
            ),
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          },
          {
            label: 'Women',
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: womenSeriesColour,
            data: state.visitorsRegistrationTrendForDashboard.map(
              (month) => month.data.femaleVisitors
            ),
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          },
          {
            label: 'Total',
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: totalSeriesColour,
            data: state.visitorsRegistrationTrendForDashboard.map((month) => month.data.total),
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          },
        ];

        const labels = state.visitorsRegistrationTrendForDashboard.map(
          (month) => month.monthCustom
        );

        return { datasets, labels };
      }
    },

    getMembershipByStatusesForDashboard: (state) => {
      if (!state.membershipByStatusesForDashboard) return {};
      else {
        const dataset = [
          {
            data: Object.values(state.membershipByStatusesForDashboard).map(
              (count) => count.brothers
            ),
            label: 'Brothers',
            backgroundColor: menSeriesColour,
            borderColor: 'transparent',
            barPercentage: 0.5,
            categoryPercentage: 1,
          },
          {
            data: Object.values(state.membershipByStatusesForDashboard).map(
              (count) => count.sisters
            ),
            label: 'Sisters',
            backgroundColor: womenSeriesColour,
            borderColor: 'transparent',
            barPercentage: 0.5,
            categoryPercentage: 1,
          },
        ];

        const labels = Object.keys(state.membershipByStatusesForDashboard).map((name) => name);

        return { dataset, labels };
      }
    },

    getMembershipByZonesForDashboard: (state) => {
      if (!state.membershipByZonesForDashboard) return {};
      else {
        const dataset = [
          {
            label: 'Brothers',
            backgroundColor: menSeriesColour,
            borderColor: 'transparent',
            data: state.membershipByZonesForDashboard.map((zone) => zone.__meta__.male_count),
            barPercentage: 0.5,
            categoryPercentage: 1,
          },
          {
            label: 'Sisters',
            backgroundColor: womenSeriesColour,
            borderColor: 'transparent',
            data: state.membershipByZonesForDashboard.map((zone) => zone.__meta__.female_count),
            barPercentage: 0.5,
            categoryPercentage: 1,
          },
        ];

        const labels = state.membershipByZonesForDashboard.map((zone) => zone.name);

        return { dataset, labels };
      }
    },

    getMembershipByMinistriesForDashboard: (state) => {
      if (!state.membershipByMinistriesForDashboard) return {};
      else {
        const dataset = [
          {
            label: 'Brothers',
            backgroundColor: menSeriesColour,
            borderColor: 'transparent',
            data: state.membershipByMinistriesForDashboard.map((min) => min.__meta__.brothers),
            barPercentage: 0.5,
            categoryPercentage: 1,
          },
          {
            label: 'Sisters',
            backgroundColor: womenSeriesColour,
            borderColor: 'transparent',
            data: state.membershipByMinistriesForDashboard.map((min) => min.__meta__.sisters),
            barPercentage: 0.5,
            categoryPercentage: 1,
          },
        ];

        const labels = state.membershipByMinistriesForDashboard.map((min) => min.name);

        return { dataset, labels };
      }
    },

    getHeadCountsTrendForDashboard: (state, getters) => {
      if (!state.headCountsTrendForDashboard) return {};
      else {
        let datasets = getters.getHeadCountTypesForDashboardOptions.map((headCount) => {
          const serviceType = getters.getDefaultServiceTypeForDashboardOptions;

          return {
            label: headCount,
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: randomColor({ luminosity: 'random' }),
            data: state.headCountsTrendForDashboard.map((month) => {
              const headCountArray = month.data?.[`${serviceType}`]?.[headCount] ?? [];
              if (headCountArray.length) {
                const sum = headCountArray.reduce((prev, cur) => prev + cur, 0);
                return Math.ceil(sum / headCountArray.length);
              } else {
                return 0;
              }
            }),
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          };
        });

        const dataSetsData = datasets.map((dataset) => dataset.data);
        const totalData = dataSetsData.reduce((prev, cur) => {
          if (!prev.length) {
            cur.forEach((count) => prev.push(count));
          } else {
            cur.forEach((count, idx) => (prev[idx] += count));
          }
          return prev;
        }, []);

        datasets = [
          ...datasets,
          {
            label: 'Total',
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: randomColor({ luminosity: 'random' }),
            data: totalData,
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          },
        ];

        const labels = state.headCountsTrendForDashboard.map((headCount) => headCount.monthCustom);

        return { datasets, labels };
      }
    },

    getAvgGivingsTrendForDashboard: (state, getters) => {
      if (!state.givingsTrendForDashboard) return {};
      else {
        let datasets = getters.getGivingTypesForDashboardOptions.map((giving) => {
          const serviceType = getters.getDefaultServiceTypeForGivingTrendDashboardOptions;

          return {
            label: giving,
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: randomColor({ luminosity: 'random' }),
            data: !serviceType
              ? []
              : state.givingsTrendForDashboard.map((month) => {
                  const givingArray = month.data?.[`${serviceType}`]?.[giving] ?? [];
                  if (givingArray.length) {
                    const sum = givingArray.reduce((prev, cur) => Number(prev) + Number(cur), 0);
                    return Math.ceil(sum / givingArray.length);
                  } else {
                    return 0;
                  }
                }),
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          };
        });

        const dataSetsData = datasets.map((dataset) => dataset.data);
        const totalData = dataSetsData.reduce((prev, cur) => {
          if (!prev.length) {
            cur.forEach((amount) => prev.push(amount));
          } else {
            cur.forEach((amount, idx) => (prev[idx] += amount));
          }
          return prev;
        }, []);

        datasets = [
          ...datasets,
          {
            label: 'Total',
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: randomColor({ luminosity: 'random' }),
            data: totalData,
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          },
        ];

        const labels = state.givingsTrendForDashboard.map((giving) => giving.monthCustom);

        return { datasets, labels };
      }
    },

    getTotalGivingsTrendForDashboard: (state, getters) => {
      if (!state.givingsTrendForDashboard) return {};
      else {
        let datasets = getters.getGivingTypesForDashboardOptions.map((giving) => {
          const serviceType = getters.getDefaultServiceTypeForGivingTrendDashboardOptions;

          return {
            label: giving,
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: randomColor({ luminosity: 'random' }),
            data: !serviceType
              ? []
              : state.givingsTrendForDashboard.map((month) => {
                  const givingArray = month.data?.[`${serviceType}`]?.[giving] ?? [];
                  if (givingArray.length) {
                    const sum = givingArray.reduce((prev, cur) => Number(prev) + Number(cur), 0);
                    return sum;
                  } else {
                    return 0;
                  }
                }),
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          };
        });

        const dataSetsData = datasets.map((dataset) => dataset.data);
        const totalData = dataSetsData.reduce((prev, cur) => {
          if (!prev.length) {
            cur.forEach((amount) => prev.push(amount));
          } else {
            cur.forEach((amount, idx) => (prev[idx] += amount));
          }
          return prev;
        }, []);

        datasets = [
          ...datasets,
          {
            label: 'Total',
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: randomColor({ luminosity: 'random' }),
            data: totalData,
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          },
        ];

        const labels = state.givingsTrendForDashboard.map((giving) => giving.monthCustom);

        return { datasets, labels };
      }
    },

    getCapturedAttendanceTrendForDashboard: (state, getters) => {
      if (!state.capturedAttendanceTrendForDashboard) return {};
      else {
        const serviceType = getters.getDefaultServiceTypeForCapturedAttendanceTrendDashboardOptions;

        const datasets = [
          {
            label: 'Men',
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: menSeriesColour,
            data: state.capturedAttendanceTrendForDashboard.map(
              (month) => month.data[serviceType]?.brothers ?? 0
            ),
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          },
          {
            label: 'Women',
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: womenSeriesColour,
            data: state.capturedAttendanceTrendForDashboard.map(
              (month) => month.data[serviceType]?.sisters ?? 0
            ),
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          },
          {
            label: 'Total',
            backgroundColor: randomColor({ luminosity: 'dark', format: 'rgba', alpha: 0.1 }),
            borderColor: totalSeriesColour,
            data: state.capturedAttendanceTrendForDashboard.map(
              (month) => month.data[serviceType]?.total ?? 0
            ),
            barPercentage: 0.5,
            categoryPercentage: 1,
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
          },
        ];

        const labels = state.capturedAttendanceTrendForDashboard.map((month) => month.monthCustom);

        return { datasets, labels };
      }
    },

    getServiceTypesForDashboardOptions: (state) => {
      if (!state?.headCountsTrendForDashboard?.length) return [];
      else {
        const sampleData = state.headCountsTrendForDashboard[0].data;
        if (isEmpty(sampleData)) {
          return [];
        }

        const serviceTypes = Object.keys(sampleData);
        return serviceTypes;
      }
    },

    getHeadCountTypesForDashboardOptions: (state) => {
      if (!state?.headCountsTrendForDashboard?.length) return [];
      else {
        const sampleData = state.headCountsTrendForDashboard[0].data;
        if (isEmpty(sampleData)) {
          return [];
        }

        let headCountTypes = Object.values(sampleData)[0];
        headCountTypes = Object.keys(headCountTypes);
        return headCountTypes;
      }
    },

    getServiceTypesForGivingsTrendDashboardOptions: (state) => {
      if (!state?.givingsTrendForDashboard?.length) return [];
      else {
        const sampleData = state.givingsTrendForDashboard[0].data;
        if (isEmpty(sampleData)) {
          return [];
        }

        const serviceTypes = Object.keys(sampleData);
        return serviceTypes;
      }
    },

    getServiceTypesForCapturedAttendanceTrendDashboardOptions: (state) => {
      if (!state?.capturedAttendanceTrendForDashboard?.length) return [];
      else {
        const sampleData = state.capturedAttendanceTrendForDashboard[0].data;
        if (isEmpty(sampleData)) {
          return [];
        }

        const serviceTypes = Object.keys(sampleData);
        return serviceTypes;
      }
    },

    getGivingTypesForDashboardOptions: (state) => {
      if (!state?.givingsTrendForDashboard?.length) return [];
      else {
        const sampleData = state.givingsTrendForDashboard[0].data;
        if (isEmpty(sampleData)) {
          return [];
        }

        let givingTypes = Object.values(sampleData)[0];
        givingTypes = Object.keys(givingTypes);
        return givingTypes;
      }
    },

    getDefaultServiceTypeForDashboardOptions: (state, getters) => {
      if (getters?.getServiceTypesForDashboardOptions?.length) {
        return null;
      }

      const serviceTypesIncludeSunday = getters.getServiceTypesForDashboardOptions.filter(
        (type) => {
          const regex = /(sunday)/;
          return regex.test(type.toLowerCase());
        }
      );

      if (serviceTypesIncludeSunday && !state.serviceTypeTrendOption) {
        return serviceTypesIncludeSunday[0];
      } else if (serviceTypesIncludeSunday && state.serviceTypeTrendOption) {
        return state.serviceTypeTrendOption;
      } else return getters.getServiceTypesForDashboardOptions[0];
    },

    getDefaultServiceTypeForGivingTrendDashboardOptions: (state, getters) => {
      if (!getters?.getServiceTypesForGivingsTrendDashboardOptions?.length) {
        return null;
      }

      const serviceTypesIncludeSunday =
        getters.getServiceTypesForGivingsTrendDashboardOptions.filter((type) => {
          const regex = /(sunday)/;
          return regex.test(type.toLowerCase());
        });

      if (serviceTypesIncludeSunday && !state.serviceTypeOptionForGivingTrend) {
        return serviceTypesIncludeSunday[0];
      } else if (serviceTypesIncludeSunday && state.serviceTypeOptionForGivingTrend) {
        return state.serviceTypeOptionForGivingTrend;
      } else return getters.getServiceTypesForGivingsTrendDashboardOptions[0];
    },

    getDefaultServiceTypeForCapturedAttendanceTrendDashboardOptions: (state, getters) => {
      if (getters?.getServiceTypesForCapturedAttendanceTrendDashboardOptions?.length) {
        return null;
      }

      const serviceTypesIncludeSunday =
        getters.getServiceTypesForCapturedAttendanceTrendDashboardOptions.filter((type) => {
          const regex = /(sunday)/;
          return regex.test(type.toLowerCase());
        });

      if (serviceTypesIncludeSunday && !state.serviceTypeOptionForCapturedAttendanceTrend) {
        return serviceTypesIncludeSunday[0];
      } else if (serviceTypesIncludeSunday && state.serviceTypeOptionForCapturedAttendanceTrend) {
        return state.serviceTypeOptionForCapturedAttendanceTrend;
      } else return getters.getServiceTypesForCapturedAttendanceTrendDashboardOptions[0];
    },
  },
  mutations: {
    setMembershipRegistrationTrendsForDashboard: (state, payload) => {
      state.membershipRegistrationTrendsForDashboard = payload;
    },

    setVisitorsRegistrationTrendForDashboard: (state, payload) => {
      state.visitorsRegistrationTrendForDashboard = payload;
    },

    setMembershipByStatusesForDashboard: (state, payload) => {
      state.membershipByStatusesForDashboard = payload;
    },

    setDashboardData: (state, payload) => (state.dashboardData = payload),

    setMembershipByZonesForDashboard: (state, payload) => {
      state.membershipByZonesForDashboard = payload;
    },

    setMembershipByMinistriesForDashboard: (state, payload) => {
      state.membershipByMinistriesForDashboard = payload;
    },

    setHeadCountsTrendForDashboard: (state, payload) => {
      state.headCountsTrendForDashboard = payload;
    },

    setGivingsTrendForDashboard: (state, payload) => {
      state.givingsTrendForDashboard = payload;
    },

    setCapturedAttendanceTrendForDashboard: (state, payload) => {
      state.capturedAttendanceTrendForDashboard = payload;
    },

    setServiceTypeTrendOption: (state, payload) => {
      state.serviceTypeTrendOption = payload;
    },

    setServiceTypeOptionForGivingTrend: (state, payload) => {
      state.serviceTypeOptionForGivingTrend = payload;
    },

    setServiceTypeOptionForCapturedAttendanceTrend: (state, payload) => {
      state.serviceTypeOptionForCapturedAttendanceTrend = payload;
    },
  },
  actions: {
    async MEMBERSHIP_REGISTRATION_TRENDS_ACTION({ commit }, { period, churchId }) {
      return new Promise(async (resolve, reject) => {
        await $http()
          .get(
            `/organisations/${churchId}/dashboard/membership-registration-trends/?period=${period}`,
            { canBeImpersonated: true }
          )
          .then((response) => {
            commit('setMembershipRegistrationTrendsForDashboard', response?.data?.data);
            resolve(response?.data?.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    async VISITORS_REGISTRATION_TREND_ACTION({ commit }, { period, churchId }) {
      return new Promise(async (resolve, reject) => {
        await $http()
          .get(
            `/organisations/${churchId}/dashboard/visitors-registration-trend/?period=${period}`,
            { canBeImpersonated: true }
          )
          .then((response) => {
            commit('setVisitorsRegistrationTrendForDashboard', response?.data?.data);
            resolve(response?.data?.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    async getDashboardDataAction({ commit }, { churchId }) {
      return await $http()
        .get(`/organisations/${churchId}/dashboardSummary`, { canBeImpersonated: true })
        .then((response) => {
          //console.log(response)
          commit('setDashboardData', response.data.data);
          return response.data.data;
        })
        .catch((error) => {
          return error;
        });
    },

    async getMembershipByStatusesForDashboardAction({ commit }, { churchId }) {
      return new Promise(async (resolve, reject) => {
        await $http()
          .get(`/organisations/${churchId}/dashboard/membership-by-statuses`, {
            canBeImpersonated: true,
          })
          .then((response) => {
            commit('setMembershipByStatusesForDashboard', response?.data?.data);
            resolve(response?.data?.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    async getMembershipByZonesForDashboardAction({ commit }, { churchId }) {
      return new Promise(async (resolve, reject) => {
        await $http()
          .get(`/organisations/${churchId}/dashboard/membership-by-zones`, {
            canBeImpersonated: true,
          })
          .then((response) => {
            commit('setMembershipByZonesForDashboard', response?.data?.data);
            resolve(response?.data?.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    async getMembershipByMinistriesForDashboardAction({ commit }, { churchId }) {
      return new Promise(async (resolve, reject) => {
        await $http()
          .get(`/organisations/${churchId}/dashboard/membership-by-ministries`, {
            canBeImpersonated: true,
          })
          .then((response) => {
            commit('setMembershipByMinistriesForDashboard', response?.data?.data);
            resolve(response?.data?.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    async HEAD_COUNTS_TREND_ACTION({ commit }, { period, churchId }) {
      return new Promise(async (resolve, reject) => {
        await $http()
          .get(`/organisations/${churchId}/dashboard/service-head-counts-trend/?period=${period}`, {
            canBeImpersonated: true,
          })
          .then((response) => {
            commit('setHeadCountsTrendForDashboard', response?.data?.data);
            resolve(response?.data?.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    async GIVINGS_TREND_ACTION({ commit }, { period, churchId }) {
      return new Promise(async (resolve, reject) => {
        await $http()
          .get(`/organisations/${churchId}/dashboard/service-givings-trend/?period=${period}`, {
            canBeImpersonated: true,
          })
          .then((response) => {
            commit('setGivingsTrendForDashboard', response?.data?.data);
            resolve(response?.data?.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    async CAPTURED_ATTENDANCE_TREND_ACTION({ commit }, { period, churchId }) {
      return new Promise(async (resolve, reject) => {
        await $http()
          .get(`/organisations/${churchId}/dashboard/captured-attendance-trend/?period=${period}`, {
            canBeImpersonated: true,
          })
          .then((response) => {
            commit('setCapturedAttendanceTrendForDashboard', response?.data?.data);
            resolve(response?.data?.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
  },
};

export default organisation_dashboard;
